import React from "react";

// Article 4 — Chronic Kidney Disease in Pregnancy: Is It Kidney Disease—or Preeclampsia?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
    {items.map((i) => (
      <Bullet key={i}>{i}</Bullet>
    ))}
  </ul>
);

export function ChronicKidneyDiseasePregnancyPreeclampsia() {
  return (
    <article className="mt-10">
      <P>
        This can be one of the most difficult diagnoses in high-risk pregnancy. Chronic kidney disease may already cause high blood pressure and protein in the urine—the same findings that can appear when preeclampsia develops.
      </P>
      <P>
        The answer is rarely found in one report. The obstetrician and nephrologist must compare <strong>the woman’s baseline, the direction of change, maternal symptoms, blood tests, placental function and fetal growth</strong>.
      </P>

      <H2>A “Normal” Creatinine May Not Be Normal for Pregnancy</H2>
      <P>Healthy pregnancy increases blood flow through the kidneys, so serum creatinine usually falls.</P>
      <P>This creates an important trap: a creatinine result may remain inside the laboratory’s non-pregnant reference range while being higher than expected for pregnancy.</P>
      <P>
        A systematic review found that creatinine values in healthy pregnancy are substantially lower than non-pregnant values and suggested that a result above approximately <strong>77 micromol/L—or 0.87 mg/dL—may be outside the expected pregnancy range</strong>. <Cite n="1" /> This is not a universal treatment threshold. The assay, previous result, gestational age, muscle mass and trend still matter.
      </P>
      <P>
        The automated <strong>eGFR</strong> printed beside creatinine should not be used to stage kidney function during pregnancy. Standard eGFR equations were developed outside pregnancy and are not validated for its altered physiology. Current renal guidance recommends following serum creatinine instead. <Cite n="2" />
      </P>

      <H2>Establish the Baseline Before There Is a Crisis</H2>
      <P>Early pregnancy records are extremely valuable.</P>
      <P>The baseline should include:</P>
      <Bullets
        items={[
          "Blood pressure.",
          "Serum creatinine.",
          "Quantified urine protein using a protein:creatinine or albumin:creatinine ratio.",
          "Full blood count and liver tests.",
          "Kidney diagnosis and previous biopsy when relevant.",
          "Medicines.",
          "Previous kidney-function trend.",
          "Previous preeclampsia or fetal growth restriction.",
        ]}
      />
      <P>Without this baseline, a later rise in blood pressure or proteinuria becomes harder to interpret.</P>
      <P>
        Protein in the urine before 20 weeks usually supports pre-existing kidney disease. It does not protect the woman from developing <strong>superimposed preeclampsia</strong> later.
      </P>

      <H2>More Protein Does Not Automatically Prove Preeclampsia</H2>
      <P>Proteinuria often increases during pregnancy in women with CKD.</P>
      <P>A higher urine-protein result may reflect:</P>
      <Bullets
        items={[
          "Normal pregnancy-related changes superimposed on CKD.",
          "Progression or flare of the kidney disease.",
          "Infection.",
          "Poor blood-pressure control.",
          "Superimposed preeclampsia.",
        ]}
      />
      <P>
        The result should be quantified, not judged from dipstick colour alone. NICE recommends interpreting proteinuria within the full clinical picture rather than using it as an isolated diagnosis. <Cite n="3" />
      </P>
      <P>
        Renal guidance advises that a substantial increase—such as a doubling from the early-pregnancy protein ratio—should prompt assessment for superimposed preeclampsia, but it is not proof by itself. <Cite n="2" />
      </P>

      <H2>What Makes Superimposed Preeclampsia More Likely?</H2>
      <P>Concern rises after 20 weeks when CKD is accompanied by new or worsening features such as:</P>
      <Bullets
        items={[
          "New hypertension.",
          "Sustained severe hypertension.",
          "A major increase in antihypertensive treatment.",
          "Falling platelets.",
          "Abnormal liver tests.",
          "Worsening kidney function.",
          "Severe headache or visual disturbance.",
          "Upper-abdominal pain.",
          "Pulmonary oedema or significant breathlessness.",
          "Fetal growth restriction.",
          "Abnormal umbilical-artery Doppler.",
          "Placental dysfunction or maternal deterioration.",
        ]}
      />
      <P>Preeclampsia is a multisystem placental disorder. Proteinuria is only one possible feature.</P>
      <P>A woman with CKD can develop preeclampsia without a dramatic new rise in urine protein.</P>

      <H2>Can Placental Biomarkers Give the Answer?</H2>
      <P>
        Placental growth factor—<strong>PlGF</strong>—and the <strong>sFlt-1/PlGF ratio</strong> can support assessment of suspected preterm preeclampsia.
      </P>
      <P>
        These tests reflect placental angiogenic balance, so they may be useful when chronic hypertension, proteinuria and kidney dysfunction make the diagnosis unclear. <Cite n="2,4" />
      </P>
      <P>But they are adjuncts, not verdicts.</P>
      <P>
        Their performance in CKD is less firmly established than in the general obstetric population, and kidney disease may influence biomarker concentrations. A reassuring result can reduce concern about placental preeclampsia for a limited period in the appropriate clinical setting; it cannot explain every rise in creatinine or replace maternal and fetal assessment. <Cite n="4" />
      </P>

      <H2>What Happens When the Diagnosis Is Still Uncertain?</H2>
      <P>The team should not wait for a perfect label while the mother or baby deteriorates.</P>
      <P>Management may include:</P>
      <Bullets
        items={[
          "Repeat blood pressure and symptom assessment.",
          "Serial creatinine, platelets and liver tests.",
          "Repeat quantified proteinuria when it may clarify change.",
          "PlGF-based testing where validated and available.",
          "Fetal growth, amniotic fluid and umbilical-artery Doppler.",
          "Fetal-heart monitoring when indicated.",
          "Nephrology and maternal-fetal-medicine review.",
          "Admission when maternal or fetal concerns are significant.",
        ]}
      />
      <P>
        The timing of birth should follow the <strong>severity and trajectory</strong> of maternal and fetal disease—not proteinuria alone.
      </P>

      <H2>Why the Baby Needs Surveillance</H2>
      <P>
        CKD is associated with higher rates of preeclampsia, preterm birth and impaired fetal growth, with risk generally increasing as kidney dysfunction, hypertension and proteinuria become more severe. A 2024 meta-analysis of more than three million pregnancies confirmed that later-stage CKD carries greater risk, although estimates varied considerably between studies. <Cite n="5" />
      </P>
      <P>Surveillance should therefore be individualised according to:</P>
      <Bullets
        items={[
          "Kidney function.",
          "Blood pressure.",
          "Proteinuria.",
          "Underlying kidney diagnosis.",
          "Fetal growth.",
          "Doppler findings.",
          "Other maternal disease.",
        ]}
      />
      <P>More appointments are not the objective. Earlier recognition of meaningful change is.</P>

      <H2>The Postpartum Result Matters</H2>
      <P>Preeclampsia may improve after placental delivery, but creatinine, hypertension or proteinuria may take time to settle.</P>
      <P>Persistent abnormalities can represent:</P>
      <Bullets
        items={[
          "Pre-existing CKD.",
          "Kidney injury related to preeclampsia.",
          "An active kidney disorder.",
          "A previously unrecognised diagnosis.",
        ]}
      />
      <P>
        Postpartum follow-up should include blood pressure, kidney function and urine protein. NICE recommends further kidney assessment when proteinuria or abnormal renal function persists after the routine postnatal review. <Cite n="3" />
      </P>
      <P>Do not assume that an abnormal kidney report is “just because of pregnancy” and will disappear without checking.</P>

      <H2>Seek Urgent Assessment</H2>
      <P>Contact maternity services urgently for:</P>
      <Bullets
        items={[
          "Severe headache.",
          "Visual disturbance.",
          "Upper-abdominal pain.",
          "Sudden breathlessness.",
          "Chest pain.",
          "Markedly reduced urine.",
          "Rapid swelling with other symptoms.",
          "Very high blood-pressure readings.",
          "Vaginal bleeding.",
          "Reduced fetal movements.",
          "Feeling seriously unwell.",
        ]}
      />

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> In CKD pregnancy, the question is not solved by one creatinine, one blood-pressure reading or one urine-protein result. A “normal” non-pregnant creatinine may be abnormal for pregnancy, rising protein may come from several causes, and superimposed preeclampsia is recognised by the complete maternal–placental–fetal pattern.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Wiles K, Bramham K, Seed PT, Nelson-Piercy C, Lightstone L, Chappell LC. Serum Creatinine in Pregnancy: A Systematic Review. Kidney International Reports. 2019;4:408–419. doi:10.1016/j.ekir.2018.10.015.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6409397/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC6409397/</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Wiles K, Chappell L, Clark K, et al. Clinical practice guideline on pregnancy and renal disease. BMC Nephrology. 2019;20:401. doi:10.1186/s12882-019-1560-2.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6822421/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC6822421/</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] National Institute for Health and Care Excellence. Hypertension in pregnancy: diagnosis and management. NICE guideline NG133. Last updated April 2023.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.nice.org.uk/guidance/ng133/chapter/Recommendations"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.nice.org.uk/guidance/ng133/chapter/Recommendations</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Ramirez Zegarra R, Ghi T, Lees C. Does the use of angiogenic biomarkers for the management of preeclampsia and fetal growth restriction improve outcomes? Challenging the current status quo. European Journal of Obstetrics &amp; Gynecology and Reproductive Biology. 2024;300:268–277. doi:10.1016/j.ejogrb.2024.07.042.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/39053087/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/39053087/</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] Jeyaraman D, Walters B, Bramham K, Fish R, Lambie M, Wu P. Adverse pregnancy outcomes in pregnant women with chronic kidney disease: a systematic review and meta-analysis. BJOG. 2024;131:1331–1340. doi:10.1111/1471-0528.17807.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/38488268/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/38488268/</a>
          </p>
        </li>
        <li id="ref-6" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[6] Kidney Disease: Improving Global Outcomes. Women and kidney health: conclusions from a KDIGO Controversies Conference. Kidney International. 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/40439632/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/40439632/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific nephrology, obstetric or fetal-medicine assessment. Do not start, stop or alter blood-pressure, kidney or aspirin treatment because of this page. Seek urgent maternity care for severe headache, visual symptoms, upper-abdominal pain, breathlessness, markedly reduced urine, very high blood pressure, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
